// CreateAccount.tsx
import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import Stepper from './Stepper';
import StepEndereco from './steps/StepAddres';
import StepBancaria from './steps/StepBank';
import StepEmpresa from './steps/StepCompany';
import StepDetalhes from './steps/StepDetails';
import { STEPS } from './steps.constants';
import type { StepKey, VersionType } from './steps.types';
import {
  Button,
  Content,
  Footer,
  PageWrapper,
  BackButton,
  ButtonsRow,
  FormWrapper,
  CustomizationToggle,
  CustomizationBody,
  CustomizationError,
} from './styled';

import { createDocument } from '../../../services/documents';
import { ensureLoginFromEnv, putKey } from '../../../services/licenseService';
import HeaderCreateAccount from '../../HeaderCreateAccount/HeaderCreateAccount';
import { Shell } from '../../atoms/Shell/Shell';
import AccessKeyModal from '../../molecules/AccessKeyModal/AccessKeyModal';
import ModalCompleteAccount from '../../molecules/ModalCompleteAccount/ModalCompleteAccont';
import { isValidCnpj } from '../../../utils/cnpj/cnpj';
import { isIntelbrasTenant } from '../../../utils/isIntelbrasTenant/isIntelbrasTenant';
import { isDashCustomizationAllowed } from '../../../utils/isDashCustomizationAllowed/isDashCustomizationAllowed';
import { useTheme } from '../../../hooks/useTheme';
import DashCustomizationSection, {
  EMPTY_DASH_CUSTOMIZATION,
  type DashCustomizationValue,
} from './DashCustomizationSection';
import { buildDashCustomizationPayload } from './buildDashCustomizationPayload';
import { getDashCustomizationError } from './getDashCustomizationError';
export default function CreateAccount() {
  const { theme } = useTheme();
  const history = useHistory();
  const location = useLocation();
  const environment = localStorage.getItem('environment');
  const user = JSON.parse(localStorage.getItem('auth-user'));
  const [step, setStep] = useState<StepKey>('user');
  const [gateOpen, setGateOpen] = useState(isIntelbrasTenant() ? true : false);
  const [openCompleteAccount, setOpenCompleteAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [licenseVersion, setLicenseVersion] = useState<VersionType>(
    isIntelbrasTenant() ? 'cve-pro' : null,
  );
  const [warn, setWarn] = useState<string | null>(null);

  // lista de steps de acordo com a versão
  const visibleSteps: readonly StepKey[] = !isIntelbrasTenant()
    ? (['user'] as const)
    : licenseVersion === 'cve-pro'
    ? STEPS
    : (['user'] as const);

  const idx = visibleSteps.indexOf(step);
  const isFirst = idx <= 0;

  const isSingleStep = visibleSteps.length === 1; // CVE-Lite

  const isLast = idx === visibleSteps.length - 1 || isSingleStep;

  const [stepValid, setStepValid] = useState(false);

  const [userData, setUserData] = useState({
    customer_name: '',
    account_name: '',
    customer_email: '',
    password: '',
    confirm_password: '',
  });

  const [addressData, setAddressData] = useState({
    cep: '',
    rua: '',
    numero: '',
    bairro: '',
    cidade: '',
    estado: '',
  });

  const [companyData, setCompanyData] = useState({
    cnpj: '',
    state_registration: '',
    company_name: '',
    company_phone: '',
    company_mail: '',
  });

  const [bankData, setBankData] = useState({
    bank: '',
    agency: '',
    account_number: '',
    account_type: 'corrente',
  });

  const [showCustomizationSettings, setShowCustomizationSettings] = useState(false);
  const [useSamePrimaryColor, setUseSamePrimaryColor] = useState(true);
  const [dashCustomization, setDashCustomization] = useState<DashCustomizationValue>(
    EMPTY_DASH_CUSTOMIZATION,
  );

  const  isIntelbras = isIntelbrasTenant();

  // garantia extra: se for CVE-Lite, nunca sair de "user"
  useEffect(() => {
    if (licenseVersion === 'cve-lite' && step !== 'user') {
      setStep('user');
    }
  }, [licenseVersion, step]);

  const next = async () => {
    // valida o step atual antes de avançar
    if (!validateStep()) return;

    if (!isLast) {
      setStep(visibleSteps[idx + 1]);
      return;
    }

    // se for o último step, conclui
    try {
      await submitAll();
      await completeAndActivate();
    } catch (e: any) {
      // `${e}` imprimiria "Error: ..."; a mensagem sozinha é o que a pessoa lê.
      setErr(e?.message || 'Falha ao concluir cadastro. Tente novamente.');
    }
  };

  const back = () => {
    if (!isFirst && !isSingleStep) {
      setStep(visibleSteps[idx - 1]);
    }
  };

  const completeAndActivate = async () => {
    setLoading(true);
    setErr(null);
    try {
      if (!isIntelbrasTenant()) {
        setOpenCompleteAccount(true);

        return;
      }

      const chave = localStorage.getItem('license-key');
      if (!chave) throw new Error('Chave não disponível');

      if (!localStorage.getItem('license-auth-token')) {
        await ensureLoginFromEnv();
      }

      await putKey({ chave, status: 'Ativada' });
      setOpenCompleteAccount(true);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Falha ao ativar a licença.';
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  const finish = () => {
    setOpenCompleteAccount(false);
    setWarn(null);
    if (isIntelbras) {
      history.push('/auth/login');
    }else{
      setUserData({
        customer_name: '',
        account_name: '',
        customer_email: '',
        password: '',
        confirm_password: '',
      });
      setDashCustomization(EMPTY_DASH_CUSTOMIZATION);
      setUseSamePrimaryColor(true);
      setShowCustomizationSettings(false);
    }  
  };

  async function submitAll() {
    try {
      setLoading(true);

      const isObjectFilled = (obj: Record<string, any>) => {
        return Object.values(obj).every(
          (value) => value !== null && value !== undefined && value !== '',
        );
      };

      const payload: any = {
        is_cve_pro: licenseVersion === 'cve-pro',
        user: location.pathname === '/auth/createaccount'
          ? userData.customer_email || process.env.REACT_APP_LICENSE_EMAIL || 'intelbras'
          : user?.email,
        environment,
        has_log: true,
        theme: theme,
        is_white_label: false,
        customer_details: {
          customer_name: userData.customer_name,
          account_name: userData.account_name,
          customer_email: userData.customer_email,
          password: userData.password,
        },
      };

      const address = {
        street: addressData.rua,
        number: addressData.numero,
        city: addressData.cidade,
        state: addressData.estado,
        zip_code: addressData.cep,
        neighborhood: addressData.bairro,
      };

      if (isObjectFilled(address)) {
        payload.address = address;
      }

      const companyInfo = {
        company_name: companyData.company_name,
        cnpj: companyData.cnpj,
        state_registration: companyData.state_registration,
        company_phone: companyData.company_phone,
        company_mail: companyData.company_mail,
      };

      if (isObjectFilled(companyInfo)) {
        payload.company_info = companyInfo;
      }

      const bankAccount = {
        bank: bankData.bank,
        agency: bankData.agency,
        account_number: bankData.account_number,
        account_type: bankData.account_type,
      };

      if (isObjectFilled(bankAccount)) {
        payload.bank_account = bankAccount;
      }

      // Opcional: só entra no body se algum campo foi preenchido (mesmo com a
      // seção recolhida). Ausente/null no body -> backend segue o fluxo normal do
      // autotenant sem chamar a customização.
      const dashCustomizationPayload = buildDashCustomizationPayload(
        dashCustomization,
        useSamePrimaryColor,
        {
          deeplinking_host: localStorage.getItem('deeplinking-host'),
          terminal_payment_host: localStorage.getItem('terminalpayment-host'),
        },
      );

      if (dashCustomizationPayload) {
        payload.dash_customization = dashCustomizationPayload;
      }

      const res = await createDocument(payload, '/tenants/provisioning/auto');
      // O createDocumentRequest já leu o JSON da resposta: `data` é o corpo
      // parseado, e é dele que saem tanto a mensagem de erro do backend quanto
      // a ressalva do subdomínio.
      const body = res?.data;

      if (!res || !res.status || res.status < 200 || res.status >= 300) {
        const message =
          body?.error || body?.message || 'Falha ao provisionar tenant';

        throw new Error(message);
      }

      const warnings = [
        // O .conf do nginx é commitado mesmo quando o DNS falha, então aqui só
        // falta o CNAME. O backend já manda a frase inteira (host a registrar e
        // o que fazer se for em outro domínio); montar texto aqui perderia isso.
        body?.subdomainError &&
          (body?.message ||
            (body?.host
              ? `não foi possível criar o subdomínio: registre o CNAME de ${body.host} na GoDaddy antes de usar a dash`
              : 'não foi possível criar o subdomínio')),
        body?.dashCustomizationError &&
          'não foi possível aplicar a personalização da dash',
      ].filter(Boolean);

      if (warnings.length) {
        setWarn(`Dashboard criado com ressalvas: ${warnings.join('; ')}`);
      }
    } catch (e) {
      throw new Error(e.message);
    } finally {
      setLoading(false);
    }
  }

  const validateStep = (): boolean => {
    if (step === 'user') {
      const {
        customer_name,
        account_name,
        customer_email,
        password,
        confirm_password,
      } = userData;

      if (
        !customer_name.trim() ||
        !account_name.trim() ||
        !customer_email.trim() ||
        !password ||
        !confirm_password
      ) {
        return false;
      }

      if (!isEmailValid(customer_email)) return false;

      if (!isPasswordValid(password)) return false;

      if (!isAccountValid(account_name)) return false;

      if (password !== confirm_password) return false;

      if (getDashCustomizationError(dashCustomization, useSamePrimaryColor)) {
        return false;
      }

      return true;
    }

    if (step === 'endereco') {
      const { cep, rua, numero, bairro, cidade, estado } = addressData;
      return !!(cep && rua && numero && bairro && cidade && estado);
    }

    if (step === 'empresa') {
      const {
        cnpj,
        state_registration,
        company_name,
        company_phone,
        company_mail,
      } = companyData;
      return !!(
        cnpj &&
        isValidCnpj(cnpj) &&
        state_registration &&
        company_name &&
        company_phone &&
        company_mail
      );
    }

    if (step === 'bancaria') {
      const { bank, agency, account_number, account_type } = bankData;
      return !!(bank && agency && account_number && account_type);
    }

    return true;
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const passwordRules = {
    length: (v: string) => v.length >= 8 && v.length <= 25,
    upper: (v: string) => /[A-Z]/.test(v),
    lower: (v: string) => /[a-z]/.test(v),
    number: (v: string) => /\d/.test(v),
    special: (v: string) => /[^A-Za-z0-9]/.test(v),
  };

  function isPasswordValid(v: string) {
    return (
      passwordRules.length(v) &&
      passwordRules.upper(v) &&
      passwordRules.lower(v) &&
      passwordRules.number(v) &&
      passwordRules.special(v)
    );
  }

  function isEmailValid(v: string) {
    return EMAIL_REGEX.test((v || '').trim());
  }

  const ACCOUNT_REGEX = /^(?![0-9 ]+$)(?!-| )[a-zA-Z0-9- ]{1,63}(?<!-| )(\.[a-zA-Z0-9- ]{1,63}(?<!-| ))*$/;

  function isAccountValid(v: string){
    return ACCOUNT_REGEX.test((v || '').trim());
  }

  useEffect(() => {
    setStepValid(validateStep());
  }, [
    step,
    userData,
    addressData,
    companyData,
    bankData,
    dashCustomization,
    useSamePrimaryColor,
  ]);

  const dashCustomizationError = getDashCustomizationError(
    dashCustomization,
    useSamePrimaryColor,
  );

  if (location.pathname === '/auth/createaccount' && !isIntelbrasTenant()) {
    return <Redirect to='/not-found' />;
  }

  return (
    <PageWrapper>
      { isIntelbras && <HeaderCreateAccount />}
      { isIntelbras ? 
        <>
          <Content
            aria-hidden={gateOpen}
            style={{
              pointerEvents: gateOpen ? 'none' : 'auto',
              filter: gateOpen ? 'blur(1px)' : 'none',
            }}
          >
            <Stepper current={step} version={licenseVersion} theme={theme} />

            {step === 'user' && (
              <StepDetalhes value={userData} onChange={setUserData} />
            )}

            {licenseVersion === 'cve-pro' && (
              <>
                {step === 'endereco' && isIntelbrasTenant() && (
                  <StepEndereco value={addressData} onChange={setAddressData} />
                )}
                {step === 'empresa' && (
                  <StepEmpresa value={companyData} onChange={setCompanyData} />
                )}
                {step === 'bancaria' && isIntelbrasTenant() && (
                  <StepBancaria value={bankData} onChange={setBankData} />
                )}
              </>
            )}
          </Content>
          <Footer
            aria-hidden={gateOpen}
            style={{
              pointerEvents: gateOpen ? 'none' : 'auto',
              filter: gateOpen ? 'blur(1px)' : 'none',
            }}
          >
              {theme ? (
                <ButtonsRow>
                  {/* Botão Anterior */}
                  {!isFirst && !isSingleStep ? (
                    <BackButton
                      onClick={back}
                      disabled={isFirst || isSingleStep || loading}
                    >
                      Anterior
                    </BackButton>
                  ) : (
                    <span />
                  )}

                  {err && (
                    <span
                      style={{
                        color: '#dc2626',
                        fontSize: 12,
                        marginRight: 12,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {err}
                    </span>
                  )}

                  <Button onClick={next} disabled={loading || !stepValid}>
                    {isLast ? (loading ? 'Enviando...' : 'Concluir') : 'Avançar'}
                  </Button>
                </ButtonsRow>
              ) : (
                <Shell height={22} width={200} />
              )}
          </Footer>
        </>
      : 
        <Content
          aria-hidden={gateOpen}
          style={{
            pointerEvents: gateOpen ? 'none' : 'auto',
            filter: gateOpen ? 'blur(1px)' : 'none',
          }}
        >
          <Stepper current={step} version={licenseVersion} theme={theme} />

          {step === 'user' && (
            <StepDetalhes value={userData} onChange={setUserData} />
          )}

          {licenseVersion === 'cve-pro' && (
            <>
              {step === 'endereco' && isIntelbrasTenant() && (
                <StepEndereco value={addressData} onChange={setAddressData} />
              )}
              {step === 'empresa' && (
                <StepEmpresa value={companyData} onChange={setCompanyData} />
              )}
              {step === 'bancaria' && isIntelbrasTenant() && (
                <StepBancaria value={bankData} onChange={setBankData} />
              )}
            </>
          )}

          {step === 'user' && isDashCustomizationAllowed() && (
            <FormWrapper>
              <CustomizationToggle
                type='button'
                onClick={() => setShowCustomizationSettings((v) => !v)}
              >
                {showCustomizationSettings ? '▾' : '▸'} Configurações de personalização
              </CustomizationToggle>

              {showCustomizationSettings && (
                <CustomizationBody>
                  <DashCustomizationSection
                    value={dashCustomization}
                    onChange={setDashCustomization}
                    useSameColor={useSamePrimaryColor}
                    onUseSameColorChange={setUseSamePrimaryColor}
                  />
                </CustomizationBody>
              )}

              {dashCustomizationError && (
                <CustomizationError>{dashCustomizationError}</CustomizationError>
              )}
            </FormWrapper>
          )}

          <ButtonsRow>
              {/* Botão Anterior */}
              {!isFirst && !isSingleStep ? (
                <BackButton
                  onClick={back}
                  disabled={isFirst || isSingleStep || loading}
                >
                  Anterior
                </BackButton>
              ) : (
                <span />
              )}

              {err && (
                <span
                  style={{
                    color: '#dc2626',
                    fontSize: 12,
                    marginRight: 12,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {err}
                </span>
              )}

              <Button onClick={next} disabled={loading || !stepValid}>
                {isLast ? (loading ? 'Enviando...' : 'Concluir') : 'Avançar'}
              </Button>
            </ButtonsRow>
        </Content>
      }

      {theme && (
        <AccessKeyModal
          open={gateOpen}
          onClose={() => setGateOpen(false)}
          onSuccess={(key, version) => {
            localStorage.setItem('license-key', key);
            setLicenseVersion(version);
            setGateOpen(false);
          }}
        />
      )}

      <ModalCompleteAccount
        open={openCompleteAccount}
        warn={warn}
        onClose={() => finish()}
      />
    </PageWrapper>
  );
}
